import { httpRequest } from 'api/httpRequest'
import { CONSTS } from 'config/objectConst'

import type {
	LinkedInConfirmProfileResult,
	LinkedInLoginHeaders,
	LinkedInLoginProfile
} from './types'

async function confirmLinkedInProfile(
	profile: LinkedInLoginProfile,
	headers: LinkedInLoginHeaders,
	correlationId?: string | null
) {
	const response = await httpRequest<LinkedInConfirmProfileResult>({
		method: 'POST',
		url: `${CONSTS.API.URL}workspace/linkedin/confirm-profile`,
		headers: flowHeaders(correlationId),
		data: {
			profile,
			headers
		}
	})
	if (!response) {
		throw new Error('LinkedIn confirm profile response is empty.')
	}
	const result = response.data
	return result
}

async function confirmLinkedInLoginSession(
	headers: LinkedInLoginHeaders,
	correlationId?: string | null
) {
	await httpRequest({
		method: 'POST',
		url: `${CONSTS.API.URL}workspace/linkedin/confirm-login-session`,
		headers: flowHeaders(correlationId),
		data: {
			headers
		}
	})
	const result = true
	return result
}

async function startLinkedInLoginV2(correlationId: string) {
	const response = await httpRequest<LinkedInLoginV2Session>({
		method: 'POST',
		url: `${CONSTS.API.URL}workspace/linkedin/login-v2`,
		headers: flowHeaders(correlationId)
	})
	if (!response) {
		throw new Error('LoginV2 response is empty.')
	}
	const result = response.data
	return result
}

async function linkedInLoginV2Document(browserSessionId: string, correlationId: string) {
	const response = await httpRequest<LinkedInLoginV2Document>({
		method: 'GET',
		url: `${CONSTS.API.URL}workspace/linkedin/login-v2/${browserSessionId}/document`,
		headers: flowHeaders(correlationId)
	})
	const mode = response?.data?.mode || response?.data?.Mode || ''
	const html = response?.data?.html || response?.data?.Html || ''
	const image = response?.data?.image || response?.data?.Image || ''
	if (!mode) {
		return null
	}

	if (mode === 'screenshot' && image) {
		const url = objectUrlFromBase64(image)
		const screenshot = { mode, html: '', url }
		return screenshot
	}

	const result = { mode, html, url: '' }
	return result
}

async function applyLinkedInLoginV2Fields(
	browserSessionId: string,
	correlationId: string,
	fields: Array<{ name: string; id: string; type: string; value: string }>,
	buttonIndex: number | null,
	buttonId?: string | null
) {
	await httpRequest({
		method: 'POST',
		url: `${CONSTS.API.URL}workspace/linkedin/login-v2/${browserSessionId}/fields`,
		headers: flowHeaders(correlationId),
		data: { fields, buttonIndex, buttonId: buttonId || null }
	})
}

function objectUrlFromBase64(image: string) {
	const binary = atob(image)
	const bytes = new Uint8Array(binary.length)
	for (let index = 0; index < binary.length; index += 1) {
		bytes[index] = binary.charCodeAt(index)
	}
	const blob = new Blob([bytes], { type: 'image/jpeg' })
	const result = URL.createObjectURL(blob)
	return result
}

async function linkedInLoginV2Click(
	browserSessionId: string,
	correlationId: string,
	x: number,
	y: number
) {
	await httpRequest({
		method: 'POST',
		url: `${CONSTS.API.URL}workspace/linkedin/login-v2/${browserSessionId}/click`,
		headers: flowHeaders(correlationId),
		data: { x, y }
	})
}

async function linkedInLoginV2Key(
	browserSessionId: string,
	correlationId: string,
	input: { key?: string; text?: string }
) {
	await httpRequest({
		method: 'POST',
		url: `${CONSTS.API.URL}workspace/linkedin/login-v2/${browserSessionId}/key`,
		headers: flowHeaders(correlationId),
		data: input
	})
}

async function linkedInLoginV2Wheel(
	browserSessionId: string,
	correlationId: string,
	deltaX: number,
	deltaY: number
) {
	await httpRequest({
		method: 'POST',
		url: `${CONSTS.API.URL}workspace/linkedin/login-v2/${browserSessionId}/wheel`,
		headers: flowHeaders(correlationId),
		data: { deltaX, deltaY }
	})
}

async function linkedInLoginV2Status(browserSessionId: string, correlationId: string) {
	const response = await httpRequest<LinkedInLoginV2Status>({
		method: 'GET',
		url: `${CONSTS.API.URL}workspace/linkedin/login-v2/${browserSessionId}`,
		headers: flowHeaders(correlationId)
	})
	if (!response) {
		throw new Error('LoginV2 status response is empty.')
	}
	const result = response.data
	return result
}

async function closeLinkedInLoginV2(browserSessionId: string, correlationId: string) {
	await httpRequest({
		method: 'DELETE',
		url: `${CONSTS.API.URL}workspace/linkedin/login-v2/${browserSessionId}`,
		headers: flowHeaders(correlationId)
	})
}

function flowHeaders(correlationId: string | null | undefined) {
	if (!correlationId) {
		return undefined
	}

	const result = { 'dz-correlationId': correlationId }
	return result
}

type LinkedInLoginV2Document = {
	mode?: string
	Mode?: string
	html?: string
	Html?: string
	image?: string
	Image?: string
}

type LinkedInLoginV2Session = {
	browserSessionId?: string
	BrowserSessionId?: string
	correlationId?: string
	CorrelationId?: string
	width?: number
	Width?: number
	height?: number
	Height?: number
}

type LinkedInLoginV2Status = {
	status?: string
	Status?: string
	headers?: LinkedInLoginHeaders | null
	Headers?: LinkedInLoginHeaders | null
	profile?: LinkedInLoginProfile | null
	Profile?: LinkedInLoginProfile | null
	message?: string | null
	Message?: string | null
}

export {
	applyLinkedInLoginV2Fields,
	linkedInLoginV2Click,
	linkedInLoginV2Document,
	linkedInLoginV2Key,
	linkedInLoginV2Status,
	linkedInLoginV2Wheel,
	closeLinkedInLoginV2,
	confirmLinkedInLoginSession,
	confirmLinkedInProfile,
	startLinkedInLoginV2
}
